import React from "react";
import {
  ClipboardSignature,
  Factory,
  House,
  Inbox,
  Paintbrush,
  Ruler,
  UsersRound,
} from "lucide-react";

const CONFIG = {
  lead_ingest: { icon: Inbox, testId: "metrics-card-lead-ingest" },
  site_measure: { icon: Ruler, testId: "metrics-card-site-measure" },
  design: { icon: Paintbrush, testId: "metrics-card-design-phase" },
  contract_sent: { icon: ClipboardSignature, testId: "metrics-card-contract-sent" },
  fabrication: { icon: Factory, testId: "metrics-card-shop-fabrication" },
  installation: { icon: UsersRound, testId: "metrics-card-crew-installation" },
  completed: { icon: House, testId: "metrics-card-job-completed" },
};

const money = value => new Intl.NumberFormat("en-CA", {
  style: "currency", currency: "CAD", notation: "compact", maximumFractionDigits: 1,
}).format(value || 0);

const secondaryText = metric => {
  if (metric.stage === "lead_ingest") return metric.response_sample_count
    ? `${Number(metric.secondary_value || 0).toFixed(1)}h avg response`
    : "No responses logged";
  if (metric.stage === "site_measure") return metric.alert_count
    ? `${metric.alert_count} missing address${metric.alert_count === 1 ? "" : "es"}`
    : "Addresses complete";
  if (metric.stage === "design") return `${money(metric.secondary_value)} open retail`;
  if (metric.stage === "contract_sent") return `${Number(metric.secondary_value || 0).toFixed(1)}d avg in stage`;
  if (metric.stage === "completed") return `${money(metric.secondary_value)} collected YTD`;
  return metric.secondary_label;
};

export const MetricsFlightControlStrip = ({ metrics, activeStage, onStageChange, interactive = true }) => (
  <section
    className="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-7"
    aria-label="Pipeline flight control"
    data-testid="dashboard-metrics-flight-control-strip"
  >
    {metrics.map(metric => {
      const config = CONFIG[metric.stage];
      const Icon = config.icon;
      const active = activeStage === metric.stage;
      return (
        <button
          key={metric.stage}
          type="button"
          tabIndex={interactive ? 0 : -1}
          aria-pressed={active}
          onClick={() => onStageChange(metric.stage)}
          className={`relative min-h-[104px] rounded-md border bg-white p-3 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[border-color,box-shadow,transform,background-color] duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_5px_14px_rgba(15,23,42,0.07)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2 ${active ? "border-slate-800 bg-slate-50 shadow-[inset_0_2px_0_#0f172a]" : "border-slate-200"}`}
          data-testid={config.testId}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-600" data-testid={`${config.testId}-label`}>{metric.label}</span>
            <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          </div>
          <div className="mt-2 flex items-end justify-between gap-2">
            <span className="text-2xl font-semibold text-slate-950" data-testid={`${config.testId}-count`}>{metric.count}</span>
            {metric.stage === "site_measure" && metric.alert_count > 0 && (
              <span className="animate-pulse rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700" data-testid="metrics-card-null-address-badge">Address</span>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-slate-500" data-testid={`${config.testId}-secondary`}>{secondaryText(metric)}</p>
        </button>
      );
    })}
  </section>
);